import type { RawParams } from "@uirouter/core";

import type { AnyObject, ColumnsType } from "@similarweb/pro-logic-components";
import {
    findAndSetColumnSortOrder,
    ShareBarCell,
    TooltipTextAnchorCell,
    TextCell,
} from "@similarweb/pro-logic-components";
import { Tooltip } from "@similarweb/ui-components";

import { getIndexCellPropsForTableV2 } from "app/components/React/Table/cells/IndexCell";
import { translate } from "app/filters/i18nFilter";
import { formatShare } from "app/filters/ngFilters";
import { numberFilter } from "app/filters/numberFilter";
import { allTrackers } from "app/services/track/track";
import { CoreWebsiteCell } from "pro-features/components/core cells/src/CoreWebsiteCell/CoreWebsiteCell";
import { displayAdvertisingNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/displayAdvertising/displayAdvertisingNavigator";
import { publisherAnalysisNavigation } from "single-spa/react-app/config/solutions25/adIntelligence/publisherAnalysis/publisherAnalysisNavigator";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { IFieldNames } from "../shared/hooks/useSwitcherLogic";
import type { ITransformedRow } from "../shared/types/tableTypes";
import type { AdIntelligenceController } from "../shared/types/types";
import type { ISortProps } from "../utils/utils";
import { NA_VALUE } from "../utils/utils";

import { IndustryFilterContainer } from "./getColumns";

interface ISwitcherData {
    dataField: IFieldNames["dataField"];
    changeField: IFieldNames["changeField"];
    isSpend: boolean;
    switcherKey: string;
}

export const getCompareColumns = (
    currentPage: number,
    pageSize: number,
    controller: AdIntelligenceController,
    resource: AdIntelligenceController,
    totalRecords: number,
    switcherData: ISwitcherData,
    sort: ISortProps,
    hasAdIntelMetrics: boolean,
    onCategoryFilterChange: (category: { id?: string }, param: keyof RawParams) => void,
) =>
    (
        [
            {
                title: "",
                align: "center",
                fixed: "left",
                key: "index",
                ...getIndexCellPropsForTableV2(currentPage, pageSize, (record: ITransformedRow) => !record.isChild),
            },
            {
                title: translate(`ad.intelligence.${controller}.${resource}s.${resource}s.column.title`, {
                    totalRecords: numberFilter()(totalRecords),
                }),
                tooltipTitle: translate(
                    `ad.intelligence.display.${controller}.${resource}s.${resource}s.column.tooltip.title`,
                ),
                tooltipSubtitle: translate(
                    `ad.intelligence.display.${controller}.${resource}s.${resource}s.column.tooltip`,
                ),
                dataIndex: resource,
                displayName: translate(`ad.intelligence.${controller}.${resource}s.${resource}s.column.picker.title`),
                width: 270,
                fixed: "left",
                hiddenInColumnPicker: true,
                render: ({ url, favicon }: Record<string, string> = {}, row: ITransformedRow) => {
                    const getWebsiteLink = (key: string) => {
                        if (resource === "publisher") {
                            return publisherAnalysisNavigation.overview.getLink({
                                ...proNavigator.getParams(),
                                key,
                            });
                        }
                        return displayAdvertisingNavigation.overview.getLink({ ...proNavigator.getParams(), key });
                    };
                    return (
                        <CoreWebsiteCell
                            domain={url}
                            icon={favicon}
                            textAndIconMargin="8px"
                            showToolTip
                            internalLink={getWebsiteLink(url)}
                            trackInternalLink={(e) => {
                                e.stopPropagation();
                                allTrackers.dispatchTrackEvent("Internal Link", "click", `Table/${url}`);
                            }}
                            childrenCount={row.children?.length}
                            spacingDomainAndAddons={false}
                            TooltipComponent={({ children }) => (
                                <Tooltip
                                    content={translate("ad.intelligence.overview.publishers.table.tooltip", {
                                        site: url,
                                    })}
                                    placement="top"
                                >
                                    {children}
                                </Tooltip>
                            )}
                        />
                    );
                },
            },
            {
                title: translate(`ad.intelligence.${controller}.${resource}s.category.column.title`),
                tooltipTitle: translate(`ad.intelligence.${controller}.${resource}s.category.column.tooltipTitle`),
                tooltipSubtitle: translate(
                    `ad.intelligence.${controller}.${resource}s.category.column.tooltipSubtitle`,
                ),
                dataIndex: "category",
                width: 180,
                hidden: true,
                render: (category: { id: string; name: string }) => (
                    <Tooltip
                        content={translate(`ad.intelligence.${controller}.${resource}s.category.filter.by`, {
                            category: category.name,
                        })}
                        enabled={!hasAdIntelMetrics ? !!category.id : null}
                    >
                        <IndustryFilterContainer>
                            {category.id ? (
                                <TooltipTextAnchorCell
                                    onClick={() => onCategoryFilterChange({ id: category.id }, "category")}
                                    dataAutomation="category"
                                    tooltipContent={translate(
                                        "ad.intelligence.advertiser.publishers.category.filter.by",
                                        {
                                            category: category.name,
                                        },
                                    )}
                                >
                                    {category.name}
                                </TooltipTextAnchorCell>
                            ) : (
                                <TextCell>{NA_VALUE}</TextCell>
                            )}
                        </IndustryFilterContainer>
                    </Tooltip>
                ),
            },
            {
                title: translate(`ad.intelligence.${controller}.${resource}s.rank.column.title`),
                tooltipTitle: translate(`ad.intelligence.${controller}.${resource}s.rank.column.tooltipTitle`),
                tooltipSubtitle: translate(`ad.intelligence.${controller}.${resource}s.rank.column.tooltipSubtitle`),
                dataIndex: "rank",
                width: 90,
                align: "right",
                hidden: true,
                render: (rank: string) => (
                    <TextCell dataAutomation="rank">{rank ? `#${numberFilter()(rank)}` : NA_VALUE}</TextCell>
                ),
            },
            {
                title: translate(
                    `ad.intelligence.advertiser.publisher.compare.${switcherData.switcherKey}.competitive.share.column.title`,
                ),
                tooltipTitle: translate(
                    `ad.intelligence.advertiser.publisher.compare.${switcherData.switcherKey}.competitive.share.column.tooltip.title`,
                ),
                tooltipSubtitle: translate(
                    `ad.intelligence.advertiser.publisher.compare.${switcherData.switcherKey}.competitive.share.column.tooltip`,
                ),
                dataIndex: switcherData.dataField,
                key: "competitiveShare",
                displayName: translate(
                    `ad.intelligence.advertiser.publisher.compare.${switcherData.switcherKey}.competitive.share.column.title`,
                ),
                width: 400,
                render: (value: { value?: Record<string, number> }) => {
                    const siteList = (proNavigator.getParams().key as string).split(",");
                    const data = siteList.map((site) => value.value?.[site]);

                    return (
                        <Tooltip
                            content={translate(
                                `ad.intelligence.${controller}.${resource}s.column.noDataForTimeframe.tooltip`,
                            )}
                            enabled={!hasAdIntelMetrics ? !value.value : null}
                        >
                            <span>
                                {value.value ? (
                                    <ShareBarCell
                                        values={data}
                                        tooltip={{
                                            title: translate(
                                                `ad.intelligence.advertiser.publisher.compare.${switcherData.switcherKey}.widgetPopupTooltip`,
                                            ),
                                            labels: siteList,
                                        }}
                                        valueFormatter={(val) => formatShare(val, 1, 0.01, NA_VALUE) as string}
                                    />
                                ) : (
                                    NA_VALUE
                                )}
                            </span>
                        </Tooltip>
                    );
                },
            },
        ] as ColumnsType<AnyObject>
    ).map((col) => findAndSetColumnSortOrder(col, sort.sort, sort.asc?.toString() === "true"));
